/* eslint-disable unicorn/filename-case -- kebab-case per docs/conventions/core.md: this file
   exports a hook, not a React component, so the PascalCase-for-components rule does not apply.
   Matches the same exemption on table-internals/use-row-keyboard-navigation.ts. */
import * as React from 'react';

interface RequestedParentIds {
  /** True when onExpandRow has already been called for this parent and no children have arrived. */
  isRequested: (parentId: string) => boolean;
  /** Records a request and returns its generation, which `clearRequested` needs to release it. */
  markRequested: (parentId: string) => number;
  /**
   * Releases a parent's outstanding request. Pass the generation `markRequested` returned to
   * release only that request: a `reportFailed` callback can outlive the fetch that created it, and
   * an unscoped release would drop a newer request's guard and let a duplicate fetch through.
   * Omit it to release whatever is current, which is what the component's own guards want.
   */
  clearRequested: (parentId: string, generation?: number) => void;
  /** Records that the consumer reported this parent as fetching, so a later falsy flag reads as a
   *  genuine transition out of loading rather than a map that merely carries false. */
  hasConsumerReportedFetching: (parentId: string) => boolean;
  /** Forgets every outstanding record whose id `stillCached` no longer reports as cached. */
  releaseUncachedRequests: (stillCached: (parentId: string) => boolean) => void;
}

/**
 * Tracks which parents this component has already asked the consumer to fetch.
 *
 * `loadingParentIds` is the consumer's own signal for the same thing, but it is optional and only
 * as accurate as the consumer keeps it. Without this, collapsing and re-expanding a row before its
 * fetch resolves leaves the cache `undefined`, which reads as "never fetched" and issues a second
 * concurrent request — breaking the documented once-per-parent contract for consumers who never
 * wired the prop.
 */
export function useRequestedParentIds(
  loadingParentIds: Readonly<Record<string, boolean>> | undefined,
): RequestedParentIds {
  const requestedGenerations = React.useRef(new Map<string, number>());
  const nextGeneration = React.useRef(0);
  const consumerFetchingIds = React.useRef(new Set<string>());

  // A committed `true` is the consumer saying a fetch is in progress, which is what lets a later
  // `false` release the record. A fetch that fails without ever committing `true` -- the ordinary
  // async shape, where both writes land in one batch -- is invisible here by construction. That is
  // what onExpandRow's reportFailed callback is for; no rule over this prop can substitute.
  React.useLayoutEffect(() => {
    if (loadingParentIds === undefined) return;
    for (const [parentId, isFetching] of Object.entries(loadingParentIds)) {
      if (isFetching) consumerFetchingIds.current.add(parentId);
    }
  }, [loadingParentIds]);

  return React.useMemo(
    () => ({
      isRequested: (parentId: string) => requestedGenerations.current.has(parentId),
      markRequested: (parentId: string) => {
        nextGeneration.current += 1;
        requestedGenerations.current.set(parentId, nextGeneration.current);
        return nextGeneration.current;
      },
      clearRequested: (parentId: string, generation?: number) => {
        if (generation !== undefined && requestedGenerations.current.get(parentId) !== generation) {
          return;
        }
        requestedGenerations.current.delete(parentId);
        consumerFetchingIds.current.delete(parentId);
      },
      hasConsumerReportedFetching: (parentId: string) => consumerFetchingIds.current.has(parentId),
      releaseUncachedRequests: (stillCached: (parentId: string) => boolean) => {
        for (const parentId of [...requestedGenerations.current.keys()]) {
          if (!stillCached(parentId)) {
            requestedGenerations.current.delete(parentId);
            consumerFetchingIds.current.delete(parentId);
          }
        }
      },
    }),
    [],
  );
}
